import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Star, Play, Sparkles, Target, Heart, Users, ChevronRight, Clock, Zap, Download, BookOpen, User, Laptop, Baby } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { IMAGES, services, transforms, packages, testimonials, offerings } from '../mock/mock';
import { submitLead } from '../services/api';
import GuideOptinDialog from '../components/GuideOptinDialog';

function Hero() {
  return (
    <section className="relative overflow-hidden bg-black">
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="Personal Training" className="w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <div className="absolute -left-40 -bottom-40 w-[600px] h-[600px] rounded-full brand-gradient opacity-25 blur-3xl" />
      </div>

      {/* Diagonal corner ribbons */}
      <div className="ribbon-tl">
        <div className="ribbon-track">
          {[...Array(2)].map((_, r) => (
            <div key={r} className="flex items-center gap-6">
              {['LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE'].map((t, i) => (
                <span key={i} className="flex items-center gap-6">{t}<span className="opacity-80">·</span></span>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="ribbon-tr">
        <div className="ribbon-track">
          {[...Array(2)].map((_, r) => (
            <div key={r} className="flex items-center gap-6">
              {['LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE', 'LIMITED SPACES AVAILABLE'].map((t, i) => (
                <span key={i} className="flex items-center gap-6">{t}<span className="opacity-80">·</span></span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-32 md:py-40 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-8">
          <div className="inline-flex items-center gap-2 border border-brand/60 bg-brand/10 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-brand backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5" /> Online Programme · November Intake
          </div>
          <h1 className="mt-6 font-display uppercase text-white leading-[0.85] text-[64px] sm:text-[96px] md:text-[132px] lg:text-[168px]">
            <span className="italic block text-brand">November</span>
            <span className="italic block text-brand">Reset.</span>
          </h1>
          <p className="mt-6 max-w-xl text-white/70 text-base md:text-lg leading-relaxed">
            Online programme built around your week. Weekly check-in with me, not an app. Nutrition that works for the whole house. Train at home or wherever you are.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/contact">
              <Button size="lg" className="bg-brand hover:bg-brand-2 text-white uppercase tracking-widest text-xs rounded-none px-7 py-6 glow-brand">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/transform">
              <Button size="lg" variant="outline" className="bg-transparent border-white/25 text-white hover:bg-white hover:text-black uppercase tracking-widest text-xs rounded-none px-7 py-6">
                <Play className="w-4 h-4 mr-2" /> See Packages
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 text-white/75 text-xs uppercase tracking-[0.2em]">
            <span className="text-brand font-semibold">PT</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>Online Programme</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>Group Sessions</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>Mum & Baby Groups</span>
          </div>
        </div>

        <div className="md:col-span-4 hidden md:block">
          <div className="relative">
            <div className="absolute -inset-3 brand-gradient opacity-40 blur-2xl" />
            <div className="relative overflow-hidden aspect-[3/4] border border-white/10">
              <img src={IMAGES.samHero} alt="Sam Turner" className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black to-transparent">
                <div className="text-[10px] tracking-[0.3em] text-brand uppercase">Meet</div>
                <div className="font-display text-2xl uppercase text-white leading-none">Sam Turner</div>
                <div className="text-xs text-white/70 mt-1">Level 3 PT · Irlam · Manchester</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="relative border-y border-white/5 bg-black/80">
        <div className="flex whitespace-nowrap animate-marquee py-4">
          {[...Array(2)].map((_, r) => (
            <div key={r} className="flex items-center gap-10 pr-10 font-display uppercase text-3xl md:text-4xl tracking-wide text-white/90 italic">
              {['Give your best', 'Get results', 'No excuses', 'Train hard', 'Eat smart', 'Stay consistent'].map((t, i) => (
                <span key={i} className="flex items-center gap-10">
                  {t}
                  <span className="text-brand">•</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const iconMap = { user: User, laptop: Laptop, users: Users, baby: Baby };
  return (
    <section className="relative bg-[#0a0a0d] py-14 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {offerings.map((s, i) => {
          const Icon = iconMap[s.icon] || User;
          return (
            <div key={i} className="reveal group flex items-center gap-4 p-4 border border-white/5 hover:border-brand transition-colors">
              <div className="w-11 h-11 flex-shrink-0 grid place-items-center brand-gradient text-white">
                <Icon className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <div className="font-display uppercase text-white text-lg leading-tight">{s.title}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/55 mt-1">{s.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="relative bg-[#0a0a0d] py-24">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="reveal text-center max-w-2xl mx-auto">
          <div className="text-[11px] tracking-[0.3em] uppercase text-brand">No Pain, No Gain</div>
          <h2 className="font-display italic text-white text-5xl md:text-7xl uppercase mt-3">Train With <span className="text-brand">Sam</span></h2>
          <p className="text-white/60 mt-4">Choose the training style that fits your life. Every program is tailored, tracked and delivered with obsessive attention to your goals.</p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <Link
              key={i}
              to={`/service/${s.slug}`}
              data-testid={`home-service-card-${s.slug}`}
              className="reveal lift group bg-[#111116] border border-white/5 hover:border-brand overflow-hidden rounded-none block"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-display text-3xl uppercase text-white leading-none">{s.title}</h3>
                  <p className="text-sm text-white/75 mt-3 leading-relaxed">{s.desc}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-brand group-hover:gap-3 transition-all">
                    Find out more <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function TransformBanner() {
  return (
    <section className="relative overflow-hidden">
      <div className="relative brand-gradient">
        <div className="absolute inset-0 opacity-25 mix-blend-overlay" style={{backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4), transparent 40%)'}} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 grid md:grid-cols-2 gap-10 items-center">
          <div className="reveal">
            <div className="text-[11px] tracking-[0.3em] uppercase text-white/90">Transform</div>
            <h2 className="font-display italic text-white text-5xl md:text-7xl uppercase mt-3 leading-[0.9]">Classes & Training</h2>
            <ul className="mt-8 space-y-3">
              {transforms.map((t) => (
                <li key={t} className="flex items-center gap-3 text-white text-lg">
                  <div className="w-6 h-6 grid place-items-center bg-black text-white"><Check className="w-4 h-4" /></div>
                  <span className="font-condensed uppercase tracking-wide text-xl">{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/transform">
              <Button size="lg" className="mt-8 bg-black hover:bg-white hover:text-black text-white uppercase tracking-widest text-xs rounded-none px-7 py-6">
                See Packages <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="reveal relative">
            <div className="absolute -inset-2 bg-black/25 blur-2xl" />
            <div className="relative aspect-[4/5] overflow-hidden border-4 border-black">
              <img src={IMAGES.warrior} alt="Total Warrior team" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-black text-white p-5 max-w-[240px] border-l-4 border-brand hidden md:block">
              <div className="font-display text-4xl leading-none">6</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-white/70 mt-1">Weekly Classes at ST Fitness</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Packages() {
  return (
    <section id="packages" className="relative py-24 bg-[#0a0a0d]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="reveal text-center max-w-2xl mx-auto">
          <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Invest In You</div>
          <h2 className="font-display italic text-white text-5xl md:text-7xl uppercase mt-3">Packages</h2>
          <p className="text-white/60 mt-4">Below are just examples of the fitness programmes available. For a tailored plan, get in touch, we build a package that fits your goals.</p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {packages.map((p, i) => (
            <Card key={i} className={`reveal lift bg-[#111116] rounded-none p-7 border ${p.highlight ? 'border-brand glow-brand' : 'border-white/5'}`}>
              <div className={`text-[10px] tracking-[0.25em] uppercase ${p.highlight ? 'text-brand' : 'text-white/50'}`}>{p.tag}</div>
              <div className="mt-3 font-display text-3xl uppercase text-white">{p.title}</div>
              <div className="mt-5 flex items-end gap-1">
                <div className="font-display text-6xl text-white leading-none">{p.price}</div>
                <div className="text-sm text-white/50 pb-2">{p.unit}</div>
              </div>
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-white/80">
                    <Check className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button className={`mt-7 w-full rounded-none uppercase tracking-widest text-xs py-5 ${p.highlight ? 'brand-gradient text-white' : 'bg-white text-black hover:bg-brand hover:text-white'}`}>
                  Get Started
                </Button>
              </Link>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="relative py-24 bg-[#0d0d10]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="reveal text-center max-w-2xl mx-auto">
          <div className="text-[11px] tracking-[0.3em] uppercase text-brand">What My Clients Say</div>
          <h2 className="font-display italic text-white text-5xl md:text-7xl uppercase mt-3">Client Testimonials</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {testimonials.map((t, i) => (
            <Card key={i} className="reveal lift bg-[#111116] border-white/5 rounded-none p-8">
              <div className="flex items-center gap-1 text-brand">{[...Array(t.rating)].map((_, r) => <Star key={r} className="w-4 h-4 fill-brand" />)}</div>
              <p className="mt-4 text-white/85 leading-relaxed text-[15px]">“{t.text}”</p>
              <div className="mt-6 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full brand-gradient grid place-items-center text-white font-bold">{t.name.charAt(0)}</div>
                <div>
                  <div className="font-semibold text-white">{t.name}</div>
                  <div className="text-xs text-white/50 uppercase tracking-wider">{t.role}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyMe() {
  const cards = [
    { icon: Target, title: 'Goal-first plans', text: 'No cookie-cutter. Every session and macro is aligned to what you want to achieve.' },
    { icon: Heart, title: 'Real accountability', text: 'Weekly check-ins, form reviews and honest feedback, no ghosting between sessions.' },
    { icon: Users, title: 'Community first', text: 'Train with people who cheer you on. From group sessions to fun team bonding events and community walks, we lift each other.' },
    { icon: Zap, title: 'Progress you can feel', text: 'Strength, energy, confidence, outcomes you notice in the gym and everywhere else.' },
  ];
  return (
    <section className="relative py-24 bg-[#0a0a0d]">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 reveal">
          <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Why train with me</div>
          <h2 className="font-display italic text-white text-5xl md:text-6xl uppercase mt-3 leading-[0.9]">A coach in your corner, <span className="text-brand">every rep</span>.</h2>
          <p className="text-white/60 mt-5 leading-relaxed">I don't sell shortcuts. I sell a plan that fits your life, a coach who shows up and a community that keeps you honest. That's how my clients keep the results they came for.</p>
          <div className="mt-6 inline-flex items-center gap-3 text-white/70 text-sm"><Clock className="w-4 h-4 text-brand" /> Book a free 20-minute discovery call</div>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {cards.map((c, i) => (
            <Card key={i} className="reveal lift bg-[#111116] border-white/5 rounded-none p-6">
              <div className="w-11 h-11 grid place-items-center brand-gradient text-white"><c.icon className="w-5 h-5" /></div>
              <div className="mt-4 font-display text-2xl uppercase text-white">{c.title}</div>
              <p className="text-white/60 text-sm mt-2 leading-relaxed">{c.text}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactCta() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Please add your name and email'); return; }
    setLoading(true);
    try {
      await submitLead({ ...form, source: 'home-cta' });
      toast.success("Thanks! Sam will be in touch shortly.");
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  };
  return (
    <section className="relative py-24 bg-[#0d0d10]">
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-10 items-center">
        <div className="reveal">
          <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Let's do this</div>
          <h2 className="font-display italic text-white text-5xl md:text-6xl uppercase mt-3 leading-[0.9]">Your journey starts here.</h2>
          <p className="text-white/60 mt-4">Step 1: Let's talk about your goals. Drop your details, we'll reply within 24 hours with the best next step for you.</p>
          <div className="mt-6 text-white/70 text-sm space-y-1.5">
            <div>· Free 20-minute discovery call</div>
            <div>· Personalised programme recommendation</div>
            <div>· No pressure, no auto-renewals</div>
          </div>
        </div>
        <form onSubmit={submit} className="reveal bg-[#111116] border border-white/5 p-6 md:p-8 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Email</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Telephone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Message</label>
            <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
          </div>
          <Button type="submit" disabled={loading} className="brand-gradient w-full text-white uppercase tracking-widest text-xs rounded-none py-6 glow-brand">{loading ? 'Sending...' : 'Submit'}</Button>
        </form>
      </div>
    </section>
  );
}

function GuideOptinSection() {
  return (
    <section className="relative py-24 overflow-hidden bg-black">
      <div className="absolute inset-0 opacity-40">
        <img src={IMAGES.nutrition3} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/60" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-10 items-center">
        <div className="reveal">
          <div className="inline-flex items-center gap-2 border border-brand/40 bg-brand/10 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-brand">
            <BookOpen className="w-3.5 h-3.5" /> Free Download
          </div>
          <h2 className="mt-5 font-display italic text-white text-5xl md:text-6xl uppercase leading-[0.9]">Grab the <span className="text-brand">7 day</span><br />starter guide</h2>
          <p className="text-white/70 mt-5 max-w-lg">A no-fluff one-pager with a 7-day training plan, plate builder and mindset rules, everything you need to make your first week count.</p>
          <ul className="mt-6 space-y-2 text-white/85 text-sm">
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand" /> Printable, saves as PDF in one tap</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand" /> Built by Sam, used with clients week in, week out</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand" /> Zero spam, unsubscribe anytime</li>
          </ul>
          <div className="mt-8">
            <GuideOptinDialog trigger={
              <Button className="brand-gradient text-white uppercase tracking-widest text-xs rounded-none px-7 py-6 glow-brand">
                <Download className="w-4 h-4 mr-2" /> Get the free guide
              </Button>
            } />
          </div>
        </div>
        <div className="reveal relative hidden md:block">
          <div className="absolute -inset-6 brand-gradient opacity-30 blur-3xl" />
          <div className="relative bg-white text-black p-8 shadow-2xl rotate-[-2deg] max-w-md ml-auto">
            <div className="text-[10px] tracking-[0.3em] uppercase text-brand font-bold">Sam Turner · Manchester</div>
            <div className="font-display italic text-4xl uppercase mt-2 leading-none">Give Your Best<br />7 Day Starter</div>
            <div className="mt-5 grid grid-cols-2 gap-2 text-[11px]">
              {['Mon Strength', 'Tue Zone 2', 'Wed Metcon', 'Thu Mobility', 'Fri Lower', 'Sat Cardio', 'Sun Reset', 'Plate rules'].map((t) => (
                <div key={t} className="border border-black/10 px-2 py-1.5 uppercase tracking-wider">{t}</div>
              ))}
            </div>
            <div className="mt-5 h-1 brand-gradient" />
            <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-black/60">st-fitness.co.uk</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <Stats />
      <Services />
      <TransformBanner />
      <Packages />
      <WhyMe />
      <Testimonials />
      <GuideOptinSection />
      <ContactCta />
    </main>
  );
}
